import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Gift, BookOpen, CalendarClock, MessageSquareReply, ExternalLink, Copy, Loader2,
  Bot, Play, Pause, Trash2, Check, Download, ImageIcon, Film, Sparkles, BadgeDollarSign,
} from "lucide-react";
import { api, apiError, BACKEND_URL } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Textarea } from "../../components/ui/textarea";

const PLATFORMS = [
  { id: "whatsapp", label: "WhatsApp" },
  { id: "facebook", label: "Facebook" },
  { id: "instagram", label: "Instagram" },
  { id: "tiktok", label: "TikTok" },
];

const copy = (text) => { navigator.clipboard?.writeText(text); toast.success("Texto copiado"); };
const openShare = (platform, caption) => {
  if (platform === "whatsapp") window.open(`https://wa.me/?text=${encodeURIComponent(caption)}`, "_blank");
  else if (platform === "facebook") { copy(caption); window.open("https://www.facebook.com/", "_blank"); }
  else if (platform === "tiktok") { copy(caption); window.open("https://www.tiktok.com/upload", "_blank"); }
  else { copy(caption); window.open("https://www.instagram.com/", "_blank"); }
};

const GUIDE = [
  {
    id: "whatsapp", title: "WhatsApp Business (gratis)", color: "text-green-400",
    intro: "Es la app de WhatsApp para negocios. Es gratis y se ve igual que el WhatsApp normal, pero te deja poner catálogo, horario y mensajes automáticos.",
    create: [
      { label: "Descargar en Android (celular Samsung, Motorola, Xiaomi, etc.)", url: "https://play.google.com/store/apps/details?id=com.whatsapp.w4b" },
      { label: "Descargar en iPhone (Apple)", url: "https://apps.apple.com/app/whatsapp-business/id1386412985" },
    ],
    steps: [
      "Abre la tienda de aplicaciones de tu celular (Play Store o App Store) o toca el botón de descarga de arriba.",
      "Busca 'WhatsApp Business' e instálala (el botón dice 'Instalar' u 'Obtener').",
      "Ábrela y acepta los permisos que te pida.",
      "Escribe tu número de teléfono y te llegará un código por mensaje. Escríbelo para confirmar.",
      "Ponle el nombre de tu negocio y una foto (puede ser tu logo o una foto tuya sonriendo).",
      "¡Listo! Ya puedes recibir y contestar mensajes de tus prospectos.",
    ],
    publish: [
      "Abre WhatsApp Business y toca el ícono de la cámara o 'Estados'.",
      "Elige la foto o el video de tu campaña.",
      "Pega el texto que te preparamos en la plataforma.",
      "Toca 'Enviar'. Tus contactos verán tu publicación durante 24 horas.",
    ],
  },
  {
    id: "facebook", title: "Facebook (gratis)", color: "text-blue-400",
    intro: "Facebook es la red social donde están tus amigos y familiares. Publicar ahí es totalmente gratis.",
    create: [
      { label: "Crear cuenta de Facebook", url: "https://www.facebook.com/r.php" },
    ],
    steps: [
      "Toca el botón 'Crear cuenta de Facebook' de arriba (o abre la app de Facebook).",
      "Escribe tu nombre y apellido.",
      "Pon tu número de celular o tu correo electrónico.",
      "Crea una contraseña (algo que recuerdes; anótala en un papel para no olvidarla).",
      "Confirma con el código que te llegue por mensaje o correo.",
      "Sube una foto tuya para que te reconozcan.",
    ],
    publish: [
      "Abre Facebook. Arriba dice '¿Qué estás pensando?'. Toca ahí.",
      "Toca 'Foto/Video' y elige la imagen o video de tu campaña.",
      "Pega el texto que te preparamos.",
      "Toca 'Publicar'. ¡Listo, ya está en tu muro gratis!",
    ],
  },
  {
    id: "instagram", title: "Instagram (gratis)", color: "text-pink-400",
    intro: "Instagram es para compartir fotos y videos. Es de la misma empresa que Facebook y también es gratis.",
    create: [
      { label: "Crear cuenta de Instagram", url: "https://www.instagram.com/accounts/emailsignup/" },
    ],
    steps: [
      "Toca 'Crear cuenta de Instagram' de arriba (o descarga la app 'Instagram' en tu celular).",
      "Escribe tu correo o tu número de celular.",
      "Crea un nombre de usuario (por ejemplo: tu nombre y tu negocio).",
      "Crea una contraseña y anótala en un papel.",
      "Confirma con el código que te llegue.",
      "Pon una foto de perfil tuya o de tu logo.",
    ],
    publish: [
      "Abre Instagram y toca el botón con el signo + (arriba o abajo).",
      "Elige la foto o el video de tu campaña.",
      "Toca la flecha 'Siguiente'.",
      "Pega el texto que te preparamos en la descripción.",
      "Toca 'Compartir'. ¡Ya está publicado gratis!",
    ],
  },
  {
    id: "tiktok", title: "TikTok (gratis)", color: "text-cyan-300",
    intro: "TikTok es la red de videos cortos con más alcance gratis: tus videos los puede ver gente que NO te conoce, sin pagar nada. Es la mejor para llegar a nuevas personas.",
    create: [
      { label: "Crear cuenta de TikTok", url: "https://www.tiktok.com/signup" },
    ],
    steps: [
      "Descarga la app 'TikTok' en tu celular (Play Store o App Store) o toca el botón de arriba.",
      "Toca 'Registrarte' y elige hacerlo con tu número de celular o tu correo.",
      "Confirma con el código que te llegue.",
      "Elige tu nombre de usuario (por ejemplo: tu nombre y tu negocio).",
      "Ve a 'Perfil' → 'Editar perfil' y pon tu foto.",
      "MUY IMPORTANTE: en 'Biografía' escribe tu enlace de embudo o tu WhatsApp. Ahí es donde tus prospectos harán clic (en TikTok los enlaces de la descripción no funcionan).",
    ],
    publish: [
      "Tu video debe ser VERTICAL (9:16), de 15 a 34 segundos, en formato MP4 (así se descarga de tu campaña). El gancho va en los primeros 3 segundos.",
      "Abre TikTok y toca el botón + del centro.",
      "Toca 'Subir' (junto al botón de grabar) y elige el video de tu campaña.",
      "Pega el texto que te preparamos como descripción (corto y con hashtags).",
      "Toca 'Publicar'. Recuerda: el enlace va en tu PERFIL, no en la descripción.",
      "Responde los comentarios con: 'la info está en el enlace de mi perfil 👆'.",
    ],
  },
];

function GuideTab() {
  return (
    <div className="space-y-6" data-testid="guide-tab">
      <div className="rounded-2xl border border-green-500/30 bg-green-500/5 p-5">
        <p className="text-sm text-zinc-300 leading-relaxed">
          Tus redes sociales personales te dejan publicar <strong className="text-white">gratis, sin pagar nada</strong>.
          Aquí te explicamos, paso a pasito y con calma, cómo crear tus cuentas y cómo publicar. No necesitas saber de
          tecnología: solo sigue los pasos en orden. 🙂
        </p>
      </div>
      {GUIDE.map((g) => (
        <div key={g.id} data-testid={`guide-${g.id}`} className="rounded-2xl border border-white/10 bg-[#121212] p-6">
          <h3 className={`font-display font-bold text-xl ${g.color}`}>{g.title}</h3>
          <p className="text-sm text-zinc-400 mt-1 mb-4">{g.intro}</p>

          <div className="flex flex-wrap gap-2 mb-5">
            {g.create.map((c) => (
              <a key={c.url} href={c.url} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold bg-yellow-400 hover:bg-yellow-300 text-black rounded-full px-4 py-2 transition-colors">
                <ExternalLink size={15} /> {c.label}
              </a>
            ))}
          </div>

          <p className="text-sm font-bold text-zinc-200 mb-2">Paso 1: Crea tu cuenta</p>
          <ol className="space-y-2 mb-5">
            {g.steps.map((s, i) => (
              <li key={`c-${s}`} className="flex items-start gap-3 text-sm text-zinc-300">
                <span className="w-6 h-6 rounded-full bg-purple-600/20 border border-purple-500/40 text-purple-300 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>

          <p className="text-sm font-bold text-zinc-200 mb-2">Paso 2: Publica (gratis)</p>
          <ol className="space-y-2">
            {g.publish.map((s, i) => (
              <li key={`p-${s}`} className="flex items-start gap-3 text-sm text-zinc-300">
                <span className="w-6 h-6 rounded-full bg-green-600/20 border border-green-500/40 text-green-300 flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        </div>
      ))}
    </div>
  );
}

function PostCard({ post, onPublished }) {
  const due = new Date(post.scheduled_for) <= new Date();
  return (
    <div data-testid="auto-post-card" className="rounded-xl border border-white/10 bg-black p-4">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${post.status === "published" ? "bg-green-500/15 text-green-300 border border-green-500/30" : due ? "bg-yellow-400/15 text-yellow-impact border border-yellow-400/30" : "bg-white/5 text-zinc-400 border border-white/10"}`}>
          {post.status === "published" ? "Publicada" : due ? "Lista para publicar hoy" : `Programada ${new Date(post.scheduled_for).toLocaleDateString()}`}
        </span>
        {(post.image_url || post.video_url) && (post.video_url ? <Film size={15} className="text-purple-400" /> : <ImageIcon size={15} className="text-purple-400" />)}
      </div>
      {post.image_url && <img src={post.image_url} alt="" className="w-full h-40 object-cover rounded-lg mb-3" />}
      {post.video_url && <video src={post.video_url} controls className="w-full h-40 object-cover rounded-lg mb-3" />}
      <p className="text-sm text-zinc-300 whitespace-pre-wrap mb-3">{post.caption}</p>
      <div className="flex flex-wrap gap-2">
        <button data-testid="copy-caption" onClick={() => copy(post.caption)} className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5"><Copy size={13} /> Copiar texto</button>
        <button onClick={() => openShare("whatsapp", post.caption)} className="inline-flex items-center gap-1.5 text-xs bg-green-600/15 hover:bg-green-600/25 text-green-300 border border-green-500/30 rounded-full px-3 py-1.5">WhatsApp</button>
        <button onClick={() => openShare("facebook", post.caption)} className="inline-flex items-center gap-1.5 text-xs bg-blue-600/15 hover:bg-blue-600/25 text-blue-300 border border-blue-500/30 rounded-full px-3 py-1.5">Facebook</button>
        {post.image_url && (
          <button
            data-testid="fb-share-image"
            onClick={() => { copy(post.caption); window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`${BACKEND_URL}/api/share/post/${post.post_id}`)}`, "_blank"); }}
            className="inline-flex items-center gap-1.5 text-xs bg-blue-600 hover:bg-blue-500 text-white font-semibold rounded-full px-3 py-1.5">
            <ImageIcon size={13} /> Publicar en Facebook con imagen
          </button>
        )}
        <button onClick={() => openShare("instagram", post.caption)} className="inline-flex items-center gap-1.5 text-xs bg-pink-600/15 hover:bg-pink-600/25 text-pink-300 border border-pink-500/30 rounded-full px-3 py-1.5">Instagram</button>
        {post.video_url && (
          <button data-testid="tiktok-share" onClick={() => openShare("tiktok", post.caption)} className="inline-flex items-center gap-1.5 text-xs bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 border border-cyan-400/30 rounded-full px-3 py-1.5">TikTok</button>
        )}
        {(post.image_url || post.video_url) && (
          <a href={post.video_url || post.image_url} target="_blank" rel="noreferrer" download className="inline-flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 rounded-full px-3 py-1.5"><Download size={13} /> Descargar</a>
        )}
        {post.status !== "published" && (
          <button data-testid="mark-published" onClick={() => onPublished(post.post_id)} className="inline-flex items-center gap-1.5 text-xs bg-yellow-400 hover:bg-yellow-300 text-black font-semibold rounded-full px-3 py-1.5"><Check size={13} /> Marcar publicada</button>
        )}
      </div>
    </div>
  );
}

function AutoTab({ minutes, reloadMinutes }) {
  const [last, setLast] = useState(null);
  const [cams, setCams] = useState([]);
  const [platforms, setPlatforms] = useState(["whatsapp", "facebook"]);
  const [postCount, setPostCount] = useState(3);
  const [cadence, setCadence] = useState(2);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/auto/last-campaign").then((r) => setLast(r.data)).catch(() => {});
    api.get("/auto/campaigns").then((r) => setCams(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const togglePlatform = (id) => setPlatforms((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const create = async () => {
    if (!platforms.length) return toast.error("Elige al menos una red social");
    setCreating(true);
    try {
      await api.post("/auto/campaigns", { platforms, post_count: postCount, cadence_days: cadence });
      toast.success("¡Campaña automática activada! Tus publicaciones están listas.");
      load(); reloadMinutes();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setCreating(false);
  };

  const toggle = async (id) => { try { await api.post(`/auto/campaigns/${id}/toggle`); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };
  const del = async (id) => { try { await api.delete(`/auto/campaigns/${id}`); toast.success("Eliminada"); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };
  const markPub = async (pid) => { try { await api.post(`/auto/posts/${pid}/published`); load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } };

  const cost = postCount * (minutes?.auto_post_cost_min ?? 1);
  const noBalance = (minutes?.balance_minutes ?? 0) < (minutes?.auto_post_cost_min ?? 1);

  return (
    <div className="space-y-6" data-testid="auto-tab">
      <div className="rounded-2xl border border-purple-500/30 bg-purple-600/5 p-5">
        <p className="text-sm text-zinc-300 leading-relaxed">
          Activa hasta <strong className="text-white">3 publicaciones automáticas</strong> usando el texto, las imágenes y el video de tu
          <strong className="text-white"> última campaña</strong>. Nosotros las preparamos y programamos; tú solo las publicas con un toque
          en tus redes (es gratis publicar). Este servicio descuenta de tu saldo de <strong className="text-yellow-impact">Minutos IA</strong>.
        </p>
      </div>

      {!last?.has_campaign ? (
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-8 text-center">
          <Sparkles className="mx-auto text-zinc-600 mb-3" size={32} />
          <p className="text-zinc-400">Primero crea una campaña con contenido para poder automatizarla.</p>
          <Link to="/dashboard/campanas"><Button className="mt-4 bg-yellow-400 hover:bg-yellow-300 text-black font-bold">Crear campaña</Button></Link>
        </div>
      ) : (
        <div className="rounded-2xl border border-white/10 bg-[#121212] p-6">
          <p className="text-xs text-zinc-500 uppercase tracking-wider font-bold mb-1">Tu última campaña</p>
          <h3 className="font-display font-bold text-lg mb-4">{last.title}</h3>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <p className="text-sm font-semibold text-zinc-300 mb-2">¿En qué redes vas a publicar?</p>
              <div className="flex flex-wrap gap-2 mb-4">
                {PLATFORMS.map((p) => (
                  <button key={p.id} data-testid={`platform-${p.id}`} onClick={() => togglePlatform(p.id)}
                    className={`text-sm rounded-full px-4 py-2 border font-medium transition-colors ${platforms.includes(p.id) ? "border-purple-500 bg-purple-600/20 text-white" : "border-white/15 bg-black text-zinc-400 hover:border-purple-500/50"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
              <label className="text-sm font-semibold text-zinc-300">Número de publicaciones</label>
              <div className="flex gap-2 mt-2 mb-4">
                {[1, 2, 3].map((n) => (
                  <button key={n} data-testid={`count-${n}`} onClick={() => setPostCount(n)} className={`w-10 h-10 rounded-lg border font-bold ${postCount === n ? "border-yellow-400 bg-yellow-400/15 text-yellow-impact" : "border-white/15 text-zinc-400"}`}>{n}</button>
                ))}
              </div>
              <label className="text-sm font-semibold text-zinc-300">Cada cuántos días publicar</label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 7].map((d) => (
                  <button key={d} onClick={() => setCadence(d)} className={`px-3 h-10 rounded-lg border font-bold text-sm ${cadence === d ? "border-yellow-400 bg-yellow-400/15 text-yellow-impact" : "border-white/15 text-zinc-400"}`}>{d === 1 ? "Diario" : `${d}d`}</button>
                ))}
              </div>
            </div>
            <div className="rounded-xl bg-black/40 border border-white/10 p-4 flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Costo (Minutos IA)</span>
                  <span className="font-bold text-white">{cost} min</span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-zinc-400">Tu saldo</span>
                  <span className={`font-bold ${noBalance ? "text-red-400" : "text-green-400"}`}>{minutes?.balance_minutes ?? 0} min</span>
                </div>
                {noBalance && <p className="text-xs text-red-400 mt-2">Sin saldo suficiente. Si llega a 0, las campañas se detienen.</p>}
              </div>
              {noBalance ? (
                <Link to="/dashboard/minutos-ia"><Button className="w-full mt-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold">Comprar Minutos IA</Button></Link>
              ) : (
                <Button data-testid="activate-auto" onClick={create} disabled={creating} className="w-full mt-3 bg-purple-600 hover:bg-purple-500 text-white font-bold">
                  {creating ? <Loader2 className="animate-spin mr-2" size={16} /> : <CalendarClock size={16} className="mr-2" />} Activar publicación automática
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {cams.map((c) => (
        <div key={c.auto_id} data-testid="auto-campaign" className="rounded-2xl border border-white/10 bg-[#121212] p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display font-bold text-lg">{c.title}</h3>
              <p className="text-xs text-zinc-500">{c.platforms.join(" · ")} · {c.posts?.length || 0} publicaciones · {c.status === "active" ? "Activa" : "Pausada"}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => toggle(c.auto_id)} className="p-2 rounded-lg border border-white/10 hover:bg-white/5" title={c.status === "active" ? "Pausar" : "Reanudar"}>
                {c.status === "active" ? <Pause size={16} /> : <Play size={16} />}
              </button>
              <button onClick={() => del(c.auto_id)} className="p-2 rounded-lg border border-white/10 hover:bg-red-500/10 text-red-400" title="Eliminar"><Trash2 size={16} /></button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(c.posts || []).map((p) => <PostCard key={p.post_id} post={p} onPublished={markPub} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function ReplyTab({ minutes, reloadMinutes }) {
  const [msg, setMsg] = useState("");
  const [out, setOut] = useState("");
  const [loading, setLoading] = useState(false);
  const noBalance = (minutes?.balance_minutes ?? 0) < (minutes?.reply_cost_min ?? 0.5);

  const generate = async () => {
    if (!msg.trim()) return toast.error("Pega el mensaje del prospecto");
    setLoading(true); setOut("");
    try {
      const { data } = await api.post("/auto/reply", { message: msg });
      setOut(data.reply); reloadMinutes();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="space-y-5" data-testid="reply-tab">
      <div className="rounded-2xl border border-purple-500/30 bg-purple-600/5 p-5">
        <p className="text-sm text-zinc-300 leading-relaxed">
          ¿Un prospecto te escribió y no sabes qué responder? Pega su mensaje aquí y nuestra IA (experta en tu campaña y su objetivo)
          te redacta al instante una respuesta cálida y persuasiva para invitarlo a WhatsApp. Cada respuesta cuesta
          <strong className="text-yellow-impact"> {minutes?.reply_cost_min ?? 0.5} min</strong> de tu saldo de Minutos IA.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-6">
        <label className="text-sm font-semibold text-zinc-300">Mensaje que te envió el prospecto</label>
        <Textarea data-testid="reply-input" value={msg} onChange={(e) => setMsg(e.target.value)} rows={4}
          placeholder="Ej: Hola, vi tu publicación y me interesa, ¿cómo funciona y cuánto cuesta empezar?"
          className="mt-2 bg-black border-white/15 focus-visible:ring-purple-500" />
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-zinc-500">Saldo: <span className={noBalance ? "text-red-400" : "text-green-400"}>{minutes?.balance_minutes ?? 0} min</span></span>
          {noBalance ? (
            <Link to="/dashboard/minutos-ia"><Button className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold">Comprar Minutos IA</Button></Link>
          ) : (
            <Button data-testid="generate-reply" onClick={generate} disabled={loading} className="bg-purple-600 hover:bg-purple-500 text-white font-bold">
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <Bot size={16} className="mr-2" />} Generar respuesta
            </Button>
          )}
        </div>

        {out && (
          <div data-testid="reply-output" className="mt-5 rounded-xl border border-green-500/30 bg-green-500/5 p-4">
            <p className="text-sm text-zinc-100 whitespace-pre-wrap">{out}</p>
            <button onClick={() => copy(out)} className="mt-3 inline-flex items-center gap-1.5 text-xs bg-green-600 hover:bg-green-500 text-white font-semibold rounded-full px-4 py-2"><Copy size={13} /> Copiar respuesta</button>
          </div>
        )}
      </div>
    </div>
  );
}

const ADS = [
  {
    id: "facebook", title: "Facebook — Promocionar publicación", color: "text-blue-400",
    requirement: "Necesitas una Página de Facebook (es gratis): abre Facebook → Menú → Páginas → Crear. Desde un perfil personal NO se puede pagar publicidad.",
    steps: [
      "Publica primero tu campaña GRATIS en tu Página (igual que lo haces en tu perfil).",
      "Debajo de tu publicación aparece el botón azul 'Promocionar publicación'. Tócalo.",
      "Objetivo: elige 'Más mensajes' (los interesados te escriben directo) o 'Más visitas al sitio web' (pega tu enlace de embudo).",
      "Público: elige tu ciudad o país, edades (por ejemplo 25 a 55) y algún interés (ej. 'negocios', 'emprendimiento', 'bienestar').",
      "Presupuesto: empieza pequeño, por ejemplo $3 USD al día durante 5 días.",
      "Agrega tu tarjeta como método de pago y toca 'Promocionar ahora'.",
      "Revisa cada día los mensajes y comentarios; responde rápido a los interesados.",
    ],
  },
  {
    id: "instagram", title: "Instagram — Promocionar", color: "text-pink-400",
    requirement: "Activa la cuenta profesional (es gratis): Perfil → Menú → Configuración → 'Tipo de cuenta y herramientas' → 'Cambiar a cuenta profesional'.",
    steps: [
      "Publica primero tu campaña GRATIS (publicación o reel).",
      "Abre tu publicación y toca el botón azul 'Promocionar'.",
      "Objetivo: 'Más visitas al perfil' o 'Más visitas al sitio web' (pega tu enlace de embudo).",
      "Público: deja el automático o personalízalo (tu ciudad, edades e intereses).",
      "Presupuesto: desde $2-3 USD al día durante 5 días para empezar.",
      "Agrega tu método de pago y confirma.",
      "Revisa los resultados en 'Ver estadísticas' y responde los mensajes que lleguen.",
    ],
  },
  {
    id: "tiktok", title: "TikTok — Promocionar (Promote)", color: "text-cyan-300",
    requirement: "Solo necesitas tu cuenta normal de TikTok y un video ya publicado. No hace falta cuenta especial.",
    steps: [
      "Publica primero tu video GRATIS (guía en la pestaña 'Guía paso a paso').",
      "Abre tu video → toca los tres puntos (...) → elige 'Promocionar'.",
      "Objetivo: 'Más visitas a un sitio web' (pega tu enlace de embudo) o 'Más visualizaciones'.",
      "Audiencia: deja la automática o define ciudad, edad e intereses.",
      "Presupuesto y duración: empieza con lo mínimo (aprox. $3 USD al día) por 3 a 5 días.",
      "Paga con tu tarjeta y listo: TikTok mostrará tu video a personas que NO te siguen.",
      "Revisa los resultados en la sección 'Promocionar' de tu perfil y responde los comentarios.",
    ],
  },
];

function AdsTab() {
  return (
    <div className="space-y-6" data-testid="ads-tab">
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-green-500/30 bg-green-500/5 p-5">
          <p className="font-bold text-green-400 mb-3">✅ Publica GRATIS cuando…</p>
          <ul className="space-y-2">
            {[
              "Estás empezando (SIEMPRE empieza gratis).",
              "Tu mensaje va dirigido a tus amigos, contactos y seguidores.",
              "Estás probando qué texto, imagen o video funciona mejor.",
              "Publicas en TikTok: su algoritmo muestra tu video gratis a desconocidos.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-zinc-300"><Check size={15} className="text-green-400 mt-0.5 shrink-0" /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="rounded-2xl border border-yellow-400/30 bg-yellow-400/5 p-5">
          <p className="font-bold text-yellow-impact mb-3">💰 Invierte en publicidad cuando…</p>
          <ul className="space-y-2">
            {[
              "Quieres llegar a personas que NO te conocen.",
              "Quieres elegir la ciudad, edad e intereses de quien ve tu anuncio.",
              "Ya probaste gratis y sabes qué publicación funciona mejor.",
              "Tienes un presupuesto pequeño disponible (desde $3 USD al día).",
            ].map((t) => (
              <li key={t} className="flex items-start gap-2 text-sm text-zinc-300"><BadgeDollarSign size={15} className="text-yellow-400 mt-0.5 shrink-0" /> {t}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="rounded-2xl border border-purple-500/30 bg-purple-600/5 p-4">
        <p className="text-sm text-zinc-300">
          🥇 <strong className="text-white">Regla de oro:</strong> invierte SOLO en la publicación que mejor te funcionó gratis
          (la que tuvo más comentarios o mensajes). Así tu dinero rinde más y no gastas adivinando.
        </p>
      </div>

      {ADS.map((a) => (
        <div key={a.id} data-testid={`ads-${a.id}`} className="rounded-2xl border border-white/10 bg-[#121212] p-6">
          <h3 className={`font-display font-bold text-xl ${a.color}`}>{a.title}</h3>
          <div className="mt-3 mb-4 rounded-xl border border-yellow-400/25 bg-yellow-400/5 p-3">
            <p className="text-sm text-zinc-300"><strong className="text-yellow-impact">Antes de empezar:</strong> {a.requirement}</p>
          </div>
          <ol className="space-y-2">
            {a.steps.map((s, i) => (
              <li key={s} className="flex items-start gap-3 text-sm text-zinc-300">
                <span className="w-6 h-6 rounded-full bg-yellow-400/15 border border-yellow-400/40 text-yellow-impact flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                {s}
              </li>
            ))}
          </ol>
        </div>
      ))}

      <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
        <p className="text-sm font-bold text-zinc-200 mb-2">💡 Consejos para no perder dinero</p>
        <ul className="space-y-1.5 text-sm text-zinc-400">
          <li>• Empieza con poco ($3 USD al día) y por pocos días (3 a 5).</li>
          <li>• Mide: ¿te llegaron mensajes o comentarios? Si sí, repite. Si no, cambia el texto o el video y vuelve a probar gratis.</li>
          <li>• Nunca pagues por 'Me gusta': paga por mensajes o visitas a tu enlace, que es lo que trae prospectos.</li>
          <li>• Responde rápido: un prospecto que espera horas se enfría.</li>
        </ul>
      </div>
    </div>
  );
}

export default function FreePublishing() {
  const [minutes, setMinutes] = useState(null);
  const [params] = useSearchParams();
  const defaultTab = ["guide", "auto", "reply", "ads"].includes(params.get("tab")) ? params.get("tab") : "guide";
  const reloadMinutes = useCallback(() => {
    api.get("/ai-minutes/me").then((r) => setMinutes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { reloadMinutes(); }, [reloadMinutes]);

  return (
    <div data-testid="free-publishing-page">
      <div className="flex items-center gap-3 mb-2">
        <Gift className="text-green-400" size={26} />
        <h1 className="font-display font-black tracking-tight text-3xl">Publicaciones sin costo</h1>
      </div>
      <p className="text-zinc-400 mb-6">Aprovecha la publicidad gratuita de tus redes sociales personales.</p>

      <Tabs defaultValue={defaultTab}>
        <TabsList className="bg-[#121212] border border-white/10 mb-6 flex-wrap h-auto">
          <TabsTrigger value="guide" data-testid="tab-guide"><BookOpen size={15} className="mr-2" /> Guía paso a paso</TabsTrigger>
          <TabsTrigger value="auto" data-testid="tab-auto"><CalendarClock size={15} className="mr-2" /> Campañas automáticas</TabsTrigger>
          <TabsTrigger value="reply" data-testid="tab-reply"><MessageSquareReply size={15} className="mr-2" /> Respuestas IA</TabsTrigger>
          <TabsTrigger value="ads" data-testid="tab-ads"><BadgeDollarSign size={15} className="mr-2" /> Publicidad con costo</TabsTrigger>
        </TabsList>
        <TabsContent value="guide"><GuideTab /></TabsContent>
        <TabsContent value="auto"><AutoTab minutes={minutes} reloadMinutes={reloadMinutes} /></TabsContent>
        <TabsContent value="reply"><ReplyTab minutes={minutes} reloadMinutes={reloadMinutes} /></TabsContent>
        <TabsContent value="ads"><AdsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
